import { useEffect, useState } from "react";
import { Star, ExternalLink } from "lucide-react";
import { reviews as defaultReviews, filterReviewsForPage, type ReviewConfig } from "@/lib/site-config";
import { getPublicReviews } from "@/lib/reviews.functions";
import { SITE_ORIGIN } from "@/lib/seo";


interface Props {
  /** Filter by service label substring (e.g. "MOT", "Servicing", "Diagnostics"). */
  service?: string;
  /** Page slug — used to filter reviews assigned to this page in admin. */
  page?: string;
  title?: string;
  /** Limit to N reviews. */
  limit?: number;
  /** Schema.org @type to wrap reviews in (e.g. "AutoRepair", "Service"). */
  itemReviewedType?: string;
  /** Name of the service or business being reviewed. */
  itemReviewedName?: string;
  /** When itemReviewedType is "Service", these populate the required fields. */
  serviceType?: string;
  areaServed?: string;
}

export function ServiceTestimonials({
  service,
  page,
  title = "What local drivers say",
  limit = 3,
  itemReviewedType = "AutoRepair",
  itemReviewedName = "Woodseer Autos",
  serviceType,
  areaServed = "East London, E14",
}: Props) {
  const [all, setAll] = useState<ReviewConfig[]>(defaultReviews);

  useEffect(() => {
    let cancelled = false;
    getPublicReviews()
      .then((r) => { if (!cancelled) setAll(r.reviews); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const pageScoped = page ? filterReviewsForPage(all, page) : all;
  const filtered = service
    ? pageScoped.filter((r) => r.service.toLowerCase().includes(service.toLowerCase()))
    : pageScoped;
  const list = (filtered.length >= limit ? filtered : pageScoped.length > 0 ? pageScoped : all).slice(0, limit);

  const avg = list.reduce((s, r) => s + r.rating, 0) / Math.max(1, list.length);

  // Per-section AggregateRating + Review payload, scoped to this service.
  const isService = itemReviewedType === "Service";
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": itemReviewedType,
    name: itemReviewedName,
    ...(isService
      ? {
          serviceType: serviceType || itemReviewedName,
          provider: { "@id": `${SITE_ORIGIN}/#business` },
          areaServed: { "@type": "Place", name: areaServed },
        }
      : {}),
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: avg.toFixed(1),
      reviewCount: String(list.length),
      bestRating: "5",
      worstRating: "1",
    },
    review: list.map((r) => ({
      "@type": "Review",
      author: { "@type": "Person", name: r.name },
      reviewRating: {
        "@type": "Rating",
        ratingValue: String(r.rating),
        bestRating: "5",
        worstRating: "1",
      },
      reviewBody: r.text,
      ...(r.sourceUrl ? { url: r.sourceUrl } : {}),
      ...(r.source
        ? {
            publisher: { "@type": "Organization", name: r.source, ...(r.sourceUrl ? { url: r.sourceUrl } : {}) },
          }
        : {}),
    })),
  };


  return (
    <section className="py-16 md:py-20 bg-card">
      <div className="mx-auto max-w-5xl px-4">
        <div className="text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground">{title}</h2>
          <div className="mt-3 flex items-center justify-center gap-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star
                key={i}
                className={`h-4 w-4 ${
                  i < Math.round(avg) ? "fill-[#FBBC04] text-[#FBBC04]" : "text-[#FBBC04]/40"
                }`}
              />
            ))}
            <span className="ml-2 text-sm font-semibold text-muted-foreground">
              {avg.toFixed(1)} / 5 from {list.length} verified Google reviews
            </span>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {list.map((r, i) => (
            <figure
              key={i}
              className="rounded-xl border border-border bg-background/60 p-5 flex flex-col"
            >
              <div className="flex gap-0.5">
                {Array.from({ length: r.rating }).map((_, j) => (
                  <Star key={j} className="h-3.5 w-3.5 fill-[#FBBC04] text-[#FBBC04]" />
                ))}
              </div>
              <blockquote className="mt-3 text-sm text-foreground/85 leading-relaxed flex-1">
                "{r.text}"
              </blockquote>
              <figcaption className="mt-4 flex items-center justify-between gap-2">
                <span className="text-xs font-semibold text-muted-foreground">
                  {r.name} · {r.service}
                </span>
                {r.sourceUrl ? (
                  <a
                    href={r.sourceUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-primary hover:underline"
                  >
                    {r.source || "Google"} <ExternalLink className="h-2.5 w-2.5" />
                  </a>
                ) : r.source ? (
                  <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{r.source}</span>
                ) : null}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>

      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
    </section>
  );
}
